/**
 * Hide menu entries that are switched OFF in Features and Pages.
 *
 * This is the piece that was missing: the catalogue's is_active flag was written
 * by the Features and Pages toggle and then never read, so switching a feature off
 * changed nothing on screen.
 *
 * Matching is by URL rather than by code, because the static menu predates the
 * catalogue and several of its items carry no catalogue code at all (User
 * Management being the one that surfaced this). The catalogue stores
 * `route_prefix` on a feature and `route_path` on a page precisely so this join is
 * possible without annotating every menu item by hand. Items whose feature has no
 * route at all — the not-yet-built screens — carry an explicit `catalogueCode`.
 *
 * IMPORTANT: this is NOT the same gate as product-tier entitlement. Tier says what
 * the organization bought; this says whether the capability is switched on at all.
 * Both must pass for an item to appear.
 */

/**
 * Split a URL into path segments, dropping the query string and any empty
 * segments so trailing/duplicate slashes cannot change the result.
 */
const segments = (url) => {
  if (typeof url !== 'string' || !url) return [];
  return url.split('?')[0].split('#')[0].split('/').filter(Boolean);
};

/**
 * Does a stored route TEMPLATE match a real URL?
 *
 * Catalogue routes are templates, not literals — `route_path` is stored as
 * `/therapist/data-collection/:childId/cold-probe`. Comparing those as plain
 * strings never matched a real URL, so switching a page off left it reachable;
 * that is the bug this replaced. A `:param` segment therefore matches any single
 * non-empty segment.
 *
 * Matching is by PREFIX: a template matches the URL it names and everything
 * beneath it, so switching off Child Profiles takes `/supervisor/child/:id/iep`
 * with it. Compared segment-wise rather than with startsWith so
 * `/admin/user-managementeer` is not treated as living under
 * `/admin/user-management`.
 */
const templateMatches = (template, url) => {
  if (!template.length) return false;
  if (template.length > url.length) return false;
  return template.every((seg, i) => seg.startsWith(':') || seg === url[i]);
};

/**
 * Build a predicate that answers "is this switched off?".
 *
 * @param {object|null} visibility Payload from GET /catalogue/visibility. NULL means
 *   NOT YET KNOWN, and nothing is hidden in that window — blanking navigation on a
 *   pending request or a network blip is far worse than briefly showing a
 *   switched-off item, and the server refuses the request regardless.
 * @param {Array<{url: string, catalogueCode: string}>} [menuRoutes] URL↔code pairs from
 *   the per-role menus (menu-items/catalogueRoutes.js). A page is reached at a
 *   different, role-prefixed URL for each role while the catalogue stores only one,
 *   so `urlHidden` alone cannot bounce a therapist off `/therapist/my-calendar` when
 *   the calendar is switched off. Passing these lets the route guard treat every URL
 *   that serves a disabled code as hidden, so the page is unreachable — not merely
 *   gone from the menu. The sidebar filter does not need them: it matches items by
 *   their own `catalogueCode` directly.
 */
export function buildVisibilityFilter(visibility, menuRoutes = []) {
  if (!visibility) return null;

  const templates = [];
  const codes = new Set();

  for (const f of visibility.disabled_features || []) {
    if (f.route_prefix) templates.push(segments(f.route_prefix));
    if (f.code) codes.add(f.code);
  }
  for (const p of visibility.disabled_pages || []) {
    if (p.route_path) templates.push(segments(p.route_path));
    if (p.code) codes.add(p.code);
  }

  // A role-prefixed URL is hidden when the code it serves is switched off, so its
  // path (and everything under it) matches just like a catalogue route would.
  for (const r of menuRoutes) {
    if (r?.url && r?.catalogueCode && codes.has(r.catalogueCode)) templates.push(segments(r.url));
  }

  if (!templates.length && !codes.size) return null;

  const urlHidden = (url) => {
    const u = segments(url);
    if (!u.length) return false;
    return templates.some((t) => templateMatches(t, u));
  };

  return {
    urlHidden,
    codeHidden: (code) => Boolean(code) && codes.has(code),
    isHidden: (item) => urlHidden(item?.url) || (Boolean(item?.catalogueCode) && codes.has(item.catalogueCode))
  };
}

/**
 * Filter an already-built menu tree (the shape NavGroup renders) by visibility.
 *
 * Used for the per-role menus, which are plain static objects rather than the
 * admin SECTIONS definition. Recurses into children and drops a group or collapse
 * that ends up empty, so switching off the only item in a section does not leave
 * an orphaned heading behind.
 */
/**
 * Remove menu items whose role grant was un-ticked in Role Definitions.
 *
 * SUBTRACTIVE ONLY, and deliberately so. It applies to the curated static menus used
 * by roles carrying is_super_admin — those roles bypass PermissionChecker, so the
 * server truthfully reports "everything granted" and their sidebar could not respond
 * to a tick. This filter reads what the role was ACTUALLY ticked for and drops the
 * rest, so un-ticking Calendar on the therapist role removes My Calendar.
 *
 * It never adds an item, so it cannot widen access for a role whose permission rows
 * are not yet seeded to action level — which is the reason those roles still carry
 * the flag.
 *
 * An item with no `catalogueCode` is left alone: it cannot be judged, and hiding on a
 * guess would silently remove screens nobody chose to remove.
 *
 * @param {string[]|null} grantedCodes from GET /catalogue/my-menu. NULL/undefined means
 *   NOT YET KNOWN (lookup in flight) → fail open, filter nothing. An EMPTY array is a
 *   real answer ("this role grants nothing") and DOES filter — that is what makes the
 *   menu strictly ADDITIVE + SUBTRACTIVE: a coded item shows iff its code is granted, so
 *   un-ticking a default hides it and ticking an extra (added to the tree) reveals it.
 *   Items with no `catalogueCode` are structural (e.g. Sync Status) and always kept.
 */
export function filterMenuByGrants(items, grantedCodes) {
  if (!Array.isArray(grantedCodes)) return items;
  const granted = new Set(grantedCodes);

  const walk = (nodes) =>
    (nodes || [])
      .filter((node) => !node?.catalogueCode || granted.has(node.catalogueCode))
      .map((node) => (node.children ? { ...node, children: walk(node.children) } : node))
      .filter((node) => !node.children || node.children.length > 0);

  return walk(items);
}

export function filterMenuByVisibility(items, visibility) {
  const filter = buildVisibilityFilter(visibility);
  if (!filter) return items;

  const walk = (nodes) =>
    (nodes || [])
      .filter((node) => !filter.isHidden(node))
      .map((node) => (node.children ? { ...node, children: walk(node.children) } : node))
      // A group or collapse whose every child went away should go too. A leaf
      // (no children key at all) is kept — `children: []` is what marks a parent.
      .filter((node) => !node.children || node.children.length > 0);

  return walk(items);
}

export default filterMenuByVisibility;
